import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import {
  Package,
  User as UserIcon,
  LogOut,
  Shield,
  ChevronRight,
  Headphones,
  Home,
  Instagram,
  Youtube,
  Sparkles,
  Heart,
} from "lucide-react";


import { useAuth } from "@/hooks/use-auth";
import { SiteHeader } from "@/components/SiteHeader";

export const Route = createFileRoute("/account/")({
  head: () => ({
    meta: [
      { title: "My Account — Jul's Skin" },
      { name: "description", content: "Manage your Jul's Skin account, orders, addresses and more." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AccountPage,
});

function AccountPage() {
  const { user, loading, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/auth" });
  }, [loading, user, navigate]);

  if (loading || !user) return null;

  const name =
    (user.user_metadata?.full_name as string | undefined) ||
    (user.email ? user.email.split("@")[0] : "there");
  const displayName = name.charAt(0).toUpperCase() + name.slice(1);
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />

      {/* Premium hero */}
      <div className="relative overflow-hidden border-b border-border">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(900px 400px at 15% -10%, color-mix(in oklab, var(--primary) 18%, transparent), transparent 60%), radial-gradient(700px 350px at 100% 0%, color-mix(in oklab, var(--primary) 10%, transparent), transparent 65%), linear-gradient(180deg, var(--background), var(--background))",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-[0.035]"
          style={{
            backgroundImage:
              "radial-gradient(currentColor 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />

        <div className="mx-auto w-full max-w-5xl px-6 pt-10 pb-14 md:pt-16 md:pb-20">
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/70 backdrop-blur px-3 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold text-primary">
              <Sparkles className="h-3 w-3" /> Members Club
            </span>
          </div>

          <div className="flex items-center gap-5 md:gap-6">
            <div className="relative shrink-0">
              <div
                className="h-20 w-20 md:h-24 md:w-24 rounded-full flex items-center justify-center text-2xl md:text-3xl font-display font-medium text-primary-foreground shadow-lg"
                style={{
                  background:
                    "linear-gradient(135deg, var(--primary), color-mix(in oklab, var(--primary) 60%, white))",
                  boxShadow:
                    "0 18px 40px -16px color-mix(in oklab, var(--primary) 55%, transparent)",
                }}
              >
                {initial}
              </div>
              <span className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-card border border-border flex items-center justify-center">
                <Sparkles className="h-3 w-3 text-primary" />
              </span>
            </div>
            <div className="min-w-0">
              <p className="eyebrow">Welcome back</p>
              <h1 className="display text-3xl md:text-5xl mt-1 truncate">{displayName}</h1>
              <p className="text-xs md:text-sm text-muted-foreground mt-1 truncate">
                {user.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-5xl px-4 md:px-6 -mt-8 pb-10 flex-1 space-y-6">
        {/* Quick stat / featured action */}
        <Link
          to="/orders"
          className="group block relative overflow-hidden rounded-2xl border border-border bg-card shadow-[0_20px_60px_-30px_rgba(15,23,42,0.25)] hover:shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)] transition-shadow"
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-60"
            style={{
              background:
                "linear-gradient(120deg, transparent 40%, color-mix(in oklab, var(--primary) 8%, transparent))",
            }}
          />
          <div className="relative flex items-center gap-4 p-5 md:p-6">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="eyebrow">Orders</p>
              <p className="font-display text-xl md:text-2xl leading-tight mt-0.5">
                Track & manage
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                View your past purchases and delivery status
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 group-hover:text-primary transition-transform" />
          </div>
        </Link>

        {/* Grid of sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Section title="Account">
            <SectionRow
              to="/account/$section"
              params={{ section: "profile" }}
              icon={<UserIcon className="h-5 w-5" />}
              title="Profile Information"
              subtitle="Name, email & preferences"
            />
            <SectionRow
              to="/wishlist"
              icon={<Heart className="h-5 w-5" />}
              title="My Wishlist"
              subtitle="Products you've saved"
            />
          </Section>


          <Section title="Help & Support">
            <SectionRow
              href="https://instagram.com"
              icon={<Instagram className="h-5 w-5" />}
              title="Instagram"
              subtitle="@julsskin"
            />
            <SectionRow
              href="https://youtube.com"
              icon={<Youtube className="h-5 w-5" />}
              title="YouTube"
              subtitle="Tutorials & stories"
            />
          </Section>
        </div>

        {isAdmin && (
          <Section title="Admin">
            <SectionRow
              to="/admin"
              icon={<Shield className="h-5 w-5" />}
              title="Admin Panel"
              subtitle="Manage store & content"
            />
          </Section>
        )}

        <button
          onClick={async () => {
            await signOut();
            navigate({ to: "/" });
          }}
          className="w-full bg-card border border-border rounded-2xl py-4 text-sm font-medium tracking-wide text-foreground hover:bg-muted hover:text-primary transition-colors flex items-center justify-center gap-2"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>

        <p className="text-center text-[11px] tracking-[0.22em] uppercase text-muted-foreground pt-2">
          Jul,s Skin · Members
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className="bg-card rounded-2xl border border-border shadow-[0_10px_40px_-24px_rgba(15,23,42,0.25)] overflow-hidden">
      {title && (
        <div className="px-5 pt-4 pb-3 flex items-center gap-2">
          <span className="h-1 w-1 rounded-full bg-primary" />
          <p className="text-[10px] font-semibold tracking-[0.24em] uppercase text-muted-foreground">
            {title}
          </p>
        </div>
      )}
      <div className="divide-y divide-border">{children}</div>
    </section>
  );
}

function SectionRow({
  to,
  params,
  href,
  icon,
  title,
  subtitle,
}: {
  to?: string;
  params?: Record<string, string>;
  href?: string;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}) {
  const inner = (
    <div className="group flex items-center gap-4 px-5 py-4 hover:bg-muted/40 transition-colors">
      <span className="h-10 w-10 rounded-xl bg-primary/8 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-1 group-hover:text-primary transition-transform" />
    </div>
  );
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer">{inner}</a>;
  return (
    <Link to={to!} params={params as any}>
      {inner}
    </Link>
  );
}
